import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  StoryTable,
  SafetyTable,
  BarrierTable,
  IdeaTable,
} from "../interfaces/interfaces";

interface PresentationContextType {
  selectedStories: StoryTable[];
  setSelectedStories: (stories: StoryTable[]) => void;
  selectedSafeties: SafetyTable[];
  setSelectedSafeties: (safeties: SafetyTable[]) => void;
  selectedBarriers: BarrierTable[];
  setSelectedBarriers: (barriers: BarrierTable[]) => void;
  selectedIdeas: IdeaTable[];
  setSelectedIdeas: (ideas: IdeaTable[]) => void;
  selectedMetrics: MetricItem[];
  setSelectedMetrics: (metrics: MetricItem[]) => void;
  selectedPresenters: PresenterItem[];
  setSelectedPresenters: (presenters: PresenterItem[]) => void;
  selectedSite: string;
  setSelectedSite: (shift: string) => void;
  selectedShift: string;
  setSelectedShift: (shift: string) => void;
  selectedDepartment: string;
  setSelectedDepartment: (department: string) => void;
  clearAll: () => void;
}

interface MetricItem {
  dept: string;
  current: number;
  target: number;
  explain: string;
}

interface PresenterItem {
  dept: string;
  name: string;
}

const PresentationContext = createContext<PresentationContextType | undefined>(
  undefined
);

export function PresentationProvider({ children }: { children: ReactNode }) {
  const [selectedStories, setSelectedStories] = useState<StoryTable[]>([]);
  const [selectedSafeties, setSelectedSafeties] = useState<SafetyTable[]>([]);
  const [selectedBarriers, setSelectedBarriers] = useState<BarrierTable[]>([]);
  const [selectedIdeas, setSelectedIdeas] = useState<IdeaTable[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<MetricItem[]>([]);
  const [selectedPresenters, setSelectedPresenters] = useState<PresenterItem[]>(
    []
  );
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [selectedShift, setSelectedShift] = useState<string>("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");

  const clearAll = () => {
    setSelectedStories([]);
    setSelectedSafeties([]);
    setSelectedBarriers([]);
    setSelectedIdeas([]);
    setSelectedMetrics([]);
  };

  return (
    <PresentationContext.Provider
      value={{
        selectedStories,
        setSelectedStories,
        selectedSafeties,
        setSelectedSafeties,
        selectedBarriers,
        setSelectedBarriers,
        selectedIdeas,
        setSelectedIdeas,
        selectedMetrics,
        setSelectedMetrics,
        selectedPresenters,
        setSelectedPresenters,
        selectedSite,
        setSelectedSite,
        selectedShift,
        setSelectedShift,
        selectedDepartment,
        setSelectedDepartment,
        clearAll,
      }}
    >
      {children}
    </PresentationContext.Provider>
  );
}

export function usePresentation() {
  const context = useContext(PresentationContext);
  if (context === undefined) {
    throw new Error(
      "usePresentation must be used within a PresentationProvider"
    );
  }
  return context;
}
